/**
 * Hook wrapper around getAccessDecision for convenient use in components.
 */

import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '@/hooks/useSubscription';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useCapability } from '@/hooks/useCapability';
import { supabase } from '@/integrations/supabase/client';
import {
  getAccessDecision,
  getAdminRequestMessage,
  AccessDecision,
  AccessContext,
  CtaAction,
} from '@/lib/accessDecision';
import { FeatureFlag } from '@/lib/subscriptionTiers';
import { toast } from 'sonner';
import { useState, useEffect } from 'react';
import { ROUTES } from '@/lib/routes';

export function useAccessDecision() {
  const {
    effectiveTier,
    subscriptionStatus,
    hasAccess,
    isPaid,
    isBetaOrg,
    startCheckout,
    openCustomerPortal,
  } = useSubscription();

  const { currentOrganization } = useOrganization();
  const { role } = useCapability();
  const navigate = useNavigate();

  // Get user email for request_admin message
  const [userEmail, setUserEmail] = useState<string | undefined>();
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUserEmail(data.session?.user?.email ?? undefined);
    });
  }, []);

  const ctx: AccessContext = useMemo(() => ({
    effectiveTier,
    subscriptionStatus,
    hasAccess,
    isPaid,
    isBetaOrg,
    userRole: role ?? undefined,
    organizationId: currentOrganization?.id,
    organizationName: currentOrganization?.name,
    userEmail,
  }), [effectiveTier, subscriptionStatus, hasAccess, isPaid, isBetaOrg, role, currentOrganization?.id, currentOrganization?.name, userEmail]);

  const checkAccess = useCallback(
    (options: { feature?: FeatureFlag; requiredRole?: string; adminOnly?: boolean; routePath?: string } = {}): AccessDecision => {
      return getAccessDecision(ctx, options);
    },
    [ctx],
  );

  const executeCta = useCallback((action: CtaAction, decision?: AccessDecision) => {
    switch (action) {
      case 'open_billing':
        navigate(ROUTES.SETTINGS_BILLING);
        break;
      case 'open_payment_portal':
        openCustomerPortal();
        break;
      case 'request_admin': {
        const msg = getAdminRequestMessage(
          { organizationName: currentOrganization?.name, userEmail },
          undefined,
          decision?.requiredTier,
        );
        navigator.clipboard.writeText(msg);
        toast.success('Message copied to clipboard. Share it with your org admin.');
        break;
      }
      case 'switch_org':
        navigate(ROUTES.DASHBOARD);
        break;
      case 'none':
      default:
        break;
    }
  }, [navigate, openCustomerPortal, currentOrganization?.name, userEmail]);

  return { checkAccess, executeCta, ctx };
}
